import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, retry, timer } from 'rxjs';
import { GetTenantIDRequest, GetTenantIDResponse } from '../../_shared/models/get-tenant-id-request';
import { GetDocumentsRequest, GetDocumentsResponse } from '../../_shared/models/get-documents-request';
import { GetDocumentsV2Response } from '../../_shared/models/get-documents-v2-response';
import { GetDatasourcesResponse } from '../../_shared/models/get-datasources-response';
import { CreateDocumentsV2Request } from '../../_shared/models/create-documents-v2-request';
import { CreateDocuments } from '../../_shared/models/create-documents-request';
import { DeleteDocumentsRequest, DeleteDocumentsResponse } from '../../_shared/models/delete-documents-request';
import { UpdateDocumentsRequest, BulkUpdateVisibilityRequest } from '../../_shared/models/update-documents-request';
import { RecrawlUrlRequest, RecrawlUrlResponse } from '../../_shared/models/recrawl-url-request';
import { UploadDocumentsRequest} from '../../_shared/models/upload-documents-request';
import { DownloadDocumentResponse, DownloadDocumentsRequest } from '../../_shared/models/download-documents-request';
import { ValidateUrlsRequest, ValidateUrlsResponse } from '../../_shared/models/validate-urls-response';


@Injectable({
  providedIn: 'root'
})
export class AbpService {
  private headers = new HttpHeaders({
    'Content-Type': 'application/json'      
  });  
  constructor(private http: HttpClient,
    @Inject('BASE_URL') private baseUrlApi: string
  ) {
  }    
  
   
  public getTenantID(request: GetTenantIDRequest): Observable<GetTenantIDResponse> {
    return this.http.post<GetTenantIDResponse>(
      `${this.baseUrlApi}api/AbpService/getTenantID`,
      request,
      { headers: this.headers }
    );
  }
  public getDocuments(request: GetDocumentsRequest): Observable<GetDocumentsResponse> {
    return this.http.post<GetDocumentsResponse>(
      `${this.baseUrlApi}api/AbpService/getDocuments`,
      request,
      { headers: this.headers }
    );
  }

  public getDocumentsV2(tenantId: string, datasourceId: string, moduleCode?: string): Observable<GetDocumentsV2Response> {
    let params = new HttpParams();
    if (moduleCode) {
      params = params.set('moduleCode', moduleCode);
    }

    return this.http.get<GetDocumentsV2Response>(
      `${this.baseUrlApi}api/AbpService/v2/knowledge/tenants/${encodeURIComponent(tenantId)}/datasources/${encodeURIComponent(datasourceId)}/documents`,
      { headers: this.headers, params }
    );
  }

  public getDatasourcesV2(tenantId: string, moduleCode: string, sourceType?: string, sortBy?: string, sortOrder?: string): Observable<GetDatasourcesResponse> {
    let params = new HttpParams().set('moduleCode', moduleCode);
    if (sourceType) {
      params = params.set('sourceType', sourceType);
    }
    if (sortBy) {
      params = params.set('sortBy', sortBy);
    }
    if (sortOrder) {
      params = params.set('sortOrder', sortOrder);
    }

    return this.http.get<GetDatasourcesResponse>(
      `${this.baseUrlApi}api/AbpService/v2/knowledge/tenants/${encodeURIComponent(tenantId)}/datasources`,
      { headers: this.headers, params }
    );
  }

  public createDocuments(request: CreateDocuments): Observable<GetDocumentsResponse> {
    return this.http.post<GetDocumentsResponse>(
      `${this.baseUrlApi}api/AbpService/createDocuments`,
      request,
      { headers: this.headers }
    );
  }

  public createDocumentsV2(tenantId: string, datasourceId: string, request: CreateDocumentsV2Request): Observable<GetDocumentsV2Response> {
    return this.http.post<GetDocumentsV2Response>(
      `${this.baseUrlApi}api/AbpService/v2/knowledge/tenants/${encodeURIComponent(tenantId)}/datasources/${encodeURIComponent(datasourceId)}/documents`,
      request,
      { headers: this.headers }
    );
  }

  public validateUrlsV2(request: ValidateUrlsRequest): Observable<ValidateUrlsResponse> {
    return this.http.post<ValidateUrlsResponse>(
      `${this.baseUrlApi}api/AbpService/v2/knowledge/validate/urls`,
      request,
      { headers: this.headers }
    ).pipe(
      retry({
        count: 3,
        delay: (error, retryCount) => {
          if (error instanceof HttpErrorResponse && (error.status === 0 || error.status === 400 || error.status === 499)) {
            console.warn(`Retrying validateUrlsV2 ${retryCount}/3`);
            return timer(1000);
          }

          throw error;
        }
      })
    );
  }

  public deleteDocuments(request: DeleteDocumentsRequest): Observable<DeleteDocumentsResponse> {
    return this.http.post<DeleteDocumentsResponse>(
      `${this.baseUrlApi}api/AbpService/deleteDocuments`,
      request,
      { headers: this.headers }
    );
  } 

  public updateDocuments(request: UpdateDocumentsRequest): Observable<GetDocumentsResponse> {
    return this.http.post<GetDocumentsResponse>(
      `${this.baseUrlApi}api/AbpService/updateDocuments`,
      request,
      { headers: this.headers }
    );
  }

  public bulkUpdateVisibility(request: BulkUpdateVisibilityRequest): Observable<GetDocumentsResponse> {
    return this.http.post<GetDocumentsResponse>(
      `${this.baseUrlApi}api/AbpService/bulkUpdateVisibility`,
      request,
      { headers: this.headers }
    );
  }

  public recrawlUrl(request: RecrawlUrlRequest): Observable<RecrawlUrlResponse> {
    return this.http.post<RecrawlUrlResponse>(
      `${this.baseUrlApi}api/AbpService/recrawlUrl`,
      request,
      { headers: this.headers }
    );
  }
  
  public uploadDocuments(request: UploadDocumentsRequest): Observable<GetDocumentsResponse> {
    const formData = new FormData();
    
    // Append files to form data
    request.files.forEach((file) => {
      formData.append('files', file);
    });

    // Append tenant ID
    formData.append('tenantId', request.tenantId);
    formData.append('moduleCode', request.moduleCode);

    // Use different headers for multipart/form-data
    const uploadHeaders = new HttpHeaders();
    // Let browser set the Content-Type with boundary
    
    return this.http.post<GetDocumentsResponse>(
      `${this.baseUrlApi}api/AbpService/uploadDocuments`,
      formData,
      { headers: uploadHeaders }
    );
  }

  public uploadDocumentsV2(tenantId: string, datasourceId: string, moduleCode: string, files: File[]): Observable<GetDocumentsV2Response> {
    const formData = new FormData();

    files.forEach((file) => {
      formData.append('files', file);
    });
    formData.append('moduleCode', moduleCode);

    // Use different headers for multipart/form-data; let the browser set the Content-Type with boundary
    const uploadHeaders = new HttpHeaders();

    return this.http.post<GetDocumentsV2Response>(
      `${this.baseUrlApi}api/AbpService/v2/knowledge/tenants/${encodeURIComponent(tenantId)}/datasources/${encodeURIComponent(datasourceId)}/uploadFiles`,
      formData,
      { headers: uploadHeaders }
    );
  }

  public downloadDocuments(request: DownloadDocumentsRequest): Observable<DownloadDocumentResponse> {
    return this.http.post<DownloadDocumentResponse>(
      `${this.baseUrlApi}api/AbpService/downloadDocuments`,
      request,
      { headers: this.headers }
    );
  }

  
}
